import type {
  GetPropertyQuery,
  PropertyAddressFragment,
  PropertyFeaturesFragment,
  PropertyFormPricingFragment,
  PropertyPoiFragment,
} from '@graphql/__generated__/graphql';
import type {
  FormDefinitionType,
  FormFieldDefinitionType,
} from '@modules/_shared/components/form/RaForm';
import { FetchDistancesButton } from '@modules/_shared/components/property-form/FetchDistancesButton';
import {
  type PropertyFormContext,
  type PropertyFormDetailsData,
  getPropertyDetailsFormDefinition,
} from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import {
  getEquipmentFormDefinition,
  getFeaturesFormDefinition,
} from '@modules/_shared/components/property-form/forms-definitions/propertyFeaturesDefinition';
import { poiFormDefinition } from '@modules/_shared/components/property-form/forms-definitions/propertyPoiDefinition';
import { pricingFormDefinition } from '@modules/_shared/components/property-form/forms-definitions/propertyPricingDefinition';
import type { IntlLocale } from '@shared/LocaleContext';

type Property = NonNullable<GetPropertyQuery['properties_by_pk']>;

export type PropertyFormData = PropertyFormDetailsData &
  Pick<Property, 'id' | 'created_at' | 'created_by'> &
  PropertyAddressFragment &
  PropertyFeaturesFragment &
  PropertyPoiFragment &
  PropertyFormPricingFragment;

export const getPropertyFormDefinition =
  (
    useMinergieCode: boolean,
    currency: string | null | undefined,
    countryCode: string,
    roomsAllowsDecimals: boolean,
    locale: IntlLocale,
    excludeCategories?: string[],
    compactMode?: boolean,
    isCMA?: boolean,
    isEsTenant?: boolean,
  ): FormDefinitionType<PropertyFormData, PropertyFormContext> =>
  ({ t, context }) => {
    const allFields: FormFieldDefinitionType<PropertyFormData>[] = [
      {
        name: 'address-title',
        label: t('Address'),
        type: 'category-title',
      },
      {
        label: t('Address'),
        type: 'address',
        gridProps: { xs: 12 },
        countryRestriction: countryCode,
      },
      ...(getPropertyDetailsFormDefinition(
        useMinergieCode,
        currency,
        countryCode,
        roomsAllowsDecimals,
        locale,
        compactMode,
        isCMA,
        isEsTenant,
      )({ t, context }) as FormFieldDefinitionType<PropertyFormData>[]),

      // *******************
      // COSTS
      // *******************
      ...pricingFormDefinition({ t, context: undefined }),

      // *******************
      // DISTANCES
      // *******************
      {
        type: 'category-title',
        name: 'distances',
        label: t('Distances'),
      },
      {
        type: 'custom',
        name: 'fetch_distances_button',
        gridProps: { xs: 12, md: 12 },
        element:
          context?.propertyId && !context.readOnly ? (
            <FetchDistancesButton propertyId={context.propertyId} />
          ) : null,
      },
      ...(poiFormDefinition({
        t,
        context: undefined,
      }) as FormFieldDefinitionType<PropertyFormData>[]),

      // *******************
      // OTHER
      // *******************
      {
        type: 'category-title',
        name: 'other',
        label: t('Other'),
      },
      ...getEquipmentFormDefinition()({ t, context: undefined }),
      ...getFeaturesFormDefinition()({ t, context: undefined }),
      {
        name: 'meta',
        label: t('Metadata'),
        type: 'category-title',
      },
      {
        name: 'created_at',
        label: t('Created at'),
        type: 'date',
        disabled: () => true,
      },
      {
        name: 'created_by',
        label: t('Created by'),
        type: 'user',
        disabled: () => true,
      },
    ];

    if (excludeCategories == null || excludeCategories.length === 0) {
      return allFields;
    }

    // Filter out excluded categories and their fields
    const filtered: FormFieldDefinitionType<PropertyFormData>[] = [];
    let skipCategory = false;

    for (const field of allFields) {
      if (field.type === 'category-title') {
        skipCategory = excludeCategories.includes(field.name);
      }
      if (!skipCategory) {
        filtered.push(field);
      }
    }

    return filtered;
  };
